/**
 * Video band: her film plays in the band itself, muted and looping, and starts
 * when the section comes into view rather than on load, so a visitor who never
 * scrolls that far never pays for it.
 */
class VideoBand extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('[data-video-player]');
    if (!this.video) return;

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-video-sound]')) this.toggleSound();
    });

    /* preload="none" means nothing is fetched until this fires. */
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.start();
          else this.video.pause();
        });
      }, { rootMargin: '200px 0px', threshold: 0.1 });
      this.observer.observe(this);
    } else {
      this.start();
    }
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  start() {
    this.video.preload = 'auto';
    const started = this.video.play();
    /* Autoplay can still be refused - low power mode, for one - and there is
       nothing useful to do about it, so the poster simply stays up. */
    if (started && typeof started.catch === 'function') started.catch(() => {});
  }

  toggleSound() {
    const turningOn = this.video.muted;
    this.video.muted = !turningOn;
    /* A browser may have parked it; turning the sound up should play it. */
    if (turningOn && this.video.paused) this.video.play().catch(() => {});

    const button = this.querySelector('[data-video-sound]');
    button.setAttribute('aria-pressed', String(turningOn));
    button.setAttribute('aria-label', turningOn ? 'Turn sound off' : 'Turn sound on');
    /* The stylesheet swaps the icons off aria-pressed. Setting .hidden here
       would do nothing: these are SVG elements, which have no such property. */
  }
}

if (!customElements.get('video-band')) {
  customElements.define('video-band', VideoBand);
}
